<template>
    <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
        <div class="flex flex-wrap items-start justify-between gap-3">
            <h3 class="text-lg font-semibold text-gray-900">Units managed</h3>
            <button
                v-if="!isPro"
                type="button"
                class="primaryButton !mt-0 shrink-0"
                @click="$emit('upgrade')"
            >
                Upgrade to Pro
            </button>
        </div>

        <p class="mt-3 text-3xl font-bold text-gray-900">
            {{ count }}<span v-if="isPro && maxLabel" class="ml-1 text-base font-normal text-gray-500">of {{ maxLabel }} in your tier</span>
            <span v-else class="ml-1 text-base font-normal text-gray-500">{{ count === 1 ? 'property' : 'properties' }}</span>
        </p>

        <template v-if="isPro">
            <div class="mt-3 h-2 w-full rounded-full bg-gray-200">
                <div
                    class="h-2 rounded-full bg-gold-900 transition-all duration-300"
                    :style="{ width: `${percent}%` }"
                    data-test="usage-bar-fill"
                ></div>
            </div>

            <p v-if="remainingLabel" class="mt-2 text-sm text-gray-600">
                {{ remainingLabel }}
            </p>

            <p v-if="nextTier" class="mt-3 rounded-md border border-[#e0cb85] bg-gold-100 px-4 py-3 text-sm text-gray-800">
                Your Pro price adjusts automatically at renewal.<template v-if="renewalDate"> On {{ renewalDate }} you'll be billed for the tier that matches your unit count that day.</template>
            </p>
        </template>
    </div>
</template>

<script>
import { usagePercent, propertiesUntilNextTier } from '@/Pages/PaymentMethods/usageBar';

export default {
    name: 'PropertiesUsageCard',
    props: {
        proPlan: {
            type: Object,
            default: null,
        },
    },
    emits: ['upgrade'],
    computed: {
        isPro() {
            return this.proPlan?.state === 'pro';
        },
        count() {
            return this.proPlan?.property_count ?? 0;
        },
        currentTier() {
            return this.proPlan?.current_tier ?? null;
        },
        nextTier() {
            return this.proPlan?.next_tier ?? null;
        },
        maxLabel() {
            const max = this.currentTier?.max;

            return max ? String(max) : '';
        },
        renewalDate() {
            return this.proPlan?.renewal_date ? new Date(this.proPlan.renewal_date).toLocaleDateString() : '';
        },
        nextTierRange() {
            const tier = this.nextTier;

            if (!tier) {
                return '';
            }

            if (!tier.max) {
                return `${tier.min || 1}+`;
            }

            return `${tier.min || 1}–${tier.max}`;
        },
        percent() {
            return usagePercent(this.count, this.currentTier?.max);
        },
        remainingLabel() {
            const remaining = propertiesUntilNextTier(this.count, this.currentTier?.max);

            if (remaining === null || !this.nextTier) {
                return '';
            }

            return `${remaining} ${remaining === 1 ? 'unit' : 'units'} left before the ${this.nextTierRange} tier`;
        },
    },
};
</script>
